
import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, LoaderCircle, HeadphoneOff, Headphones, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAudioData } from './AudioContext';
import { decodedToken } from '../../utils/auth';
import { submitAudioBuzzRecord } from '../../services/apiService';
import Swal from "sweetalert2";


const AudioVerification = () => {
  const [selectedOptions, setSelectedOptions] = useState({}); 
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);

  // New state variables for frequency management
  const [currentBasicFrequency, setCurrentBasicFrequency] = useState('--');
  const [selectedRiskyFrequency, setSelectedRiskyFrequency] = useState('');
  const [audioJudgments, setAudioJudgments] = useState({}); // Track judgment per audio file
  const [condition2Judgments, setCondition2Judgments] = useState({}); 
  const [availableFrequencies, setAvailableFrequencies] = useState([]);
  const [frequencyToAudioMap, setFrequencyToAudioMap] = useState({}); // Map frequency to audio index
  const [replayMediaCompleted, setReplayMediaCompleted] = useState(false); 
  const [playingSource, setPlayingSource] = useState(''); 

  const navigate = useNavigate();

  const { calibrationData, detectionResults, originalAudioFiles, humanJudgment, hasBuzzDetected, setDetectionResults, setOriginalAudioFiles, setHumanJudgment } = useAudioData();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const decoded = decodedToken();
  const userEmail = decoded?.email || '';

  // Audio player ref
  const audioPlayerRef = useRef(null);

  const [setHasBuzzDetected] = useState(false);
  const [buzzFiles, setBuzzFiles] = useState([]);

  const [isPaused, setIsPaused] = useState(false);
  const stopRequestedRef = useRef(false);

  const showSubmit =
    !(buzzFiles.length > 1 && currentPage < buzzFiles.length - 1);

  const totalPages = buzzFiles.length;

  // Show only 2 pages at a time
  const getVisiblePages = () => {
    if (totalPages <= 2) return [0, 1].slice(0, totalPages);

    if (currentPage === totalPages - 1) {
      return [currentPage - 1, currentPage];
    }

    return [currentPage, currentPage + 1];
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, buzzFiles.length - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    setIsLoading(true);
  }, [currentPage]);

  // Extract available frequencies from detection results - ONLY BASE FREQUENCIES
  useEffect(() => {
    let resultsArray;
    
    if (Array.isArray(detectionResults)) {
      resultsArray = detectionResults;
    } else if (detectionResults && detectionResults.apiResults) {
      resultsArray = detectionResults.apiResults;
    } else {
      resultsArray = [];
    }

    // Extract ONLY base frequencies (not harmonics)
    const frequenciesSet = new Set();
    const freqToIndexMap = {};

    resultsArray.forEach((item, index) => {
      if (item?.result?.base_frequency) {
        const baseFreq = item.result.base_frequency;
        frequenciesSet.add(baseFreq);
        
        // Map frequency to audio file index
        if (!freqToIndexMap[baseFreq]) {
          freqToIndexMap[baseFreq] = [];
        }
        freqToIndexMap[baseFreq].push(index);
      }
    });

    const frequencies = Array.from(frequenciesSet).sort((a, b) => a - b);
    setAvailableFrequencies(frequencies);
    setFrequencyToAudioMap(freqToIndexMap);
    
    console.log('Available base frequencies:', frequencies);
    console.log('Frequency to audio map:', freqToIndexMap);
  }, [detectionResults]);

  useEffect(() => {
    // Only filter buzz files if buzz was detected
    if (hasBuzzDetected) {
      let resultsArray;

      if (Array.isArray(detectionResults)) {
        resultsArray = detectionResults;
      } else if (detectionResults && detectionResults.apiResults) {
        resultsArray = detectionResults.apiResults;
      } else {
        resultsArray = [];
      }

      // Filter files with buzz detected - check for "Buzz" or "Severe Buzz" prediction
      const filesWithBuzz = resultsArray.filter(item =>
        item.success &&
        item.result?.prediction &&
        (item.result.prediction === "Buzz" || item.result.prediction === "Severe Buzz")
      );

      setBuzzFiles(filesWithBuzz);
      setCurrentPage(0);
    }
  }, [detectionResults, hasBuzzDetected]);

  // Auto-update audioJudgments based on condition2Judgments for No Buzz UI
  useEffect(() => {
    if (!hasBuzzDetected) {
      const hasAnyBuzz = Object.values(condition2Judgments).some(judgment => judgment === 'buzz');
      
      // Build audioJudgments from condition2Judgments
      const newAudioJudgments = {};
      
      Object.entries(condition2Judgments).forEach(([freq, judgment]) => {
        const audioIndices = frequencyToAudioMap[parseInt(freq)];
        if (audioIndices && audioIndices.length > 0) {
          const audioIndex = audioIndices[0];
          newAudioJudgments[audioIndex] = {
            judgment: judgment === 'buzz',
            frequency: judgment === 'buzz' ? parseInt(freq) : null
          };
        }
      });
      
      setAudioJudgments(newAudioJudgments);
    }
  }, [condition2Judgments, frequencyToAudioMap, hasBuzzDetected]);

  const handleReplay = async () => {
    if (!originalAudioFiles || originalAudioFiles.length === 0) {
      alert('No audio files available for replay');
      return;
    }

    stopRequestedRef.current = false;
    setIsPlaying(true);
    setPlayingSource('replayMedia');
    setIsPaused(false);
    setAudioPlayed(true);
    setReplayMediaCompleted(false);

    for (let i = 0; i < originalAudioFiles.length; i++) {
      if (stopRequestedRef.current) break;

      setCurrentPlayingIndex(i);
      
      // Get the base frequency for current file from detection results
      let resultsArray;
      if (Array.isArray(detectionResults)) {
        resultsArray = detectionResults;
      } else if (detectionResults && detectionResults.apiResults) {
        resultsArray = detectionResults.apiResults;
      } else {
        resultsArray = [];
      }

      if (resultsArray[i]?.result?.base_frequency) {
        setCurrentBasicFrequency(`${resultsArray[i].result.base_frequency} Hz`);
      }

      await playAudioFile(originalAudioFiles[i].file);
    }

    setIsPlaying(false);
    setPlayingSource('');
    setCurrentPlayingIndex(0);
    setCurrentBasicFrequency('--');
    
    // Mark replay as completed
    if (!stopRequestedRef.current) {
      setReplayMediaCompleted(true);
    }
  };

  const togglePause = () => {
    if (!audioPlayerRef.current) return;

    if (isPaused) {
      audioPlayerRef.current.play();
      setIsPaused(false);
    } else {
      audioPlayerRef.current.pause();
      setIsPaused(true);
    }
  };

// Play risky frequency for No Buzz UI
const handleReplayRiskyFrequencyNoBuzz = async () => {
  if (!selectedRiskyFrequency) {
    alert('Please select a frequency from the dropdown first');
    return;
  }

  // Get the audio indices for this frequency
  const audioIndices = frequencyToAudioMap[parseInt(selectedRiskyFrequency)];
  
  if (!audioIndices || audioIndices.length === 0) {
    alert('No audio file found for the selected frequency');
    return;
  }

  // Use the first matching audio index
  const audioIndex = audioIndices[0];

  // Find the corresponding audio file
  let resultsArray;
  if (Array.isArray(detectionResults)) {
    resultsArray = detectionResults;
  } else if (detectionResults && detectionResults.apiResults) {
    resultsArray = detectionResults.apiResults;
  } else {
    resultsArray = [];
  }

  const matchingResult = resultsArray[audioIndex];

  if (matchingResult?.result?.original_audio_s3_url) {
    setIsPlaying(true);
    setPlayingSource('riskyFreq');
    setAudioPlayed(true);

    try {
      await playAudioFromUrl(matchingResult.result.original_audio_s3_url);
    } catch (error) {
      // Only show error if it's not a stop action
      if (!stopRequestedRef.current) {
        console.error('Error playing audio:', error);
        alert('Failed to play audio');
      }
    }

    setIsPlaying(false);
    setPlayingSource('');
  } else if (originalAudioFiles[audioIndex]) {
    // Fallback to local file
    setIsPlaying(true);
    setPlayingSource('riskyFreq');
    setAudioPlayed(true);
    
    try {
      await playAudioFile(originalAudioFiles[audioIndex].file);
    } catch (error) {
      // Only show error if it's not a stop action
      if (!stopRequestedRef.current) {
        console.error('Error playing audio:', error);
        alert('Failed to play audio');
      }
    }

    setIsPlaying(false);
    setPlayingSource('');
  } else {
    alert('No audio file found for the selected frequency');
  }
};


  // Play risky basic frequency (for buzz detected case)
  const handleReplayRiskyFrequency = async () => {
    if (!buzzFiles || buzzFiles.length === 0) return;

    const currentBuzzFile = buzzFiles[currentPage];
    if (!currentBuzzFile?.result?.original_audio_s3_url) {
      alert('No audio URL available');
      return;
    }

    setIsPlaying(true);
    setAudioPlayed(true);

    try {
      await playAudioFromUrl(currentBuzzFile.result.original_audio_s3_url);
    } catch (error) {
      console.error('Error playing audio:', error);
      alert('Failed to play audio');
    }

    setIsPlaying(false);
  };

  const playAudioFile = (file) => {
    return new Promise((resolve) => {
      if (!file) return resolve();

      const url = URL.createObjectURL(file);

      if (!audioPlayerRef.current) {
        audioPlayerRef.current = new Audio();
      }

      audioPlayerRef.current.src = url;
      audioPlayerRef.current.load();

      audioPlayerRef.current.oncanplaythrough = () => {
        audioPlayerRef.current.play();
      };

      audioPlayerRef.current.onended = () => {
        URL.revokeObjectURL(url);
        resolve();
      };

      audioPlayerRef.current.onerror = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
    });
  };

  // Play audio from URL (S3)
  const playAudioFromUrl = (url) => {
    return new Promise((resolve, reject) => {
      if (!url) {
        resolve();
        return;
      }

      if (!audioPlayerRef.current) {
        audioPlayerRef.current = new Audio();
      }

      audioPlayerRef.current.src = url;
      audioPlayerRef.current.load();

    // Flag to track if playback was stopped
    let wasStopped = false;

    const stopHandler = () => {
      wasStopped = true;
      resolve(); // Resolve instead of rejecting
    };

    // Listen for stop via stopPlayback function
    audioPlayerRef.current.addEventListener('abort', stopHandler);
    audioPlayerRef.current.addEventListener('pause', stopHandler);

      audioPlayerRef.current.oncanplaythrough = () => {
    if (!wasStopped) {
        audioPlayerRef.current.play().catch((err) => {
          if (!wasStopped) {
            reject(err);
          } else {
            resolve();
          }
        });
      }
      };

      audioPlayerRef.current.onended = () => {
      audioPlayerRef.current.removeEventListener('abort', stopHandler);
      audioPlayerRef.current.removeEventListener('pause', stopHandler);
        resolve();
      };

      audioPlayerRef.current.onerror = (e) => {
      audioPlayerRef.current.removeEventListener('abort', stopHandler);
      audioPlayerRef.current.removeEventListener('pause', stopHandler);
      if (!wasStopped) {
        reject(new Error('Failed to load audio'));
      } else {
        resolve();
      }
      };
    });
  };

  const stopPlayback = () => {
    stopRequestedRef.current = true;

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      audioPlayerRef.current.src = '';
    }

    setIsPlaying(false);
    setPlayingSource('');
    setIsPaused(false);
    setCurrentPlayingIndex(0);
    setCurrentBasicFrequency('--');
  };

  // Handle Condition 2 judgment
  const handleCondition2Judgment = (judgment) => {
    if (!selectedRiskyFrequency) return;

    const freq = parseInt(selectedRiskyFrequency);

    setCondition2Judgments(prev => {
      const updated = { ...prev };
      
      // Toggle: if same button clicked, deselect
      if (updated[freq] === judgment) {
        delete updated[freq];
      } else {
        updated[freq] = judgment;
      }
      
      return updated;
    });
  };

  const handleBackClick = () => {
    stopPlayback();
    navigate('/audiobuzz-detection');
  };

  const handleSubmit = async () => {
  // Different validation for buzz UI vs no buzz UI
  if (!hasBuzzDetected) {
    // No Buzz UI validation - check if replay completed and has judgment
    if (!replayMediaCompleted) {
      await Swal.fire({
        icon: "warning",
        title: "Replay Required",
        text: "Please replay all media files first using 'Replay Media' button.",
        confirmButtonColor: "#14b8a6",
        background: "#ffffff",
        color: "#000000",
      });
      return;
    }
  } else {
    // Buzz UI validation - check if current page has selection
    if (!selectedOptions[currentPage]) {
      await Swal.fire({
        icon: "warning",
        title: "Selection Required",
        text: "Please select an option for the current record first.",
        confirmButtonColor: "#14b8a6",
        background: "#ffffff",
        color: "#000000",
      });
      return;
    }
    }

    setIsSubmitting(true);

    try {
      let resultsArray;

      if (Array.isArray(detectionResults)) {
        resultsArray = detectionResults;
      } else if (detectionResults && detectionResults.apiResults) {
        resultsArray = detectionResults.apiResults;
      } else {
        resultsArray = [];
      }

      const audioRecordingInterface = resultsArray
        .map((item, index) => {
          const recordId = item?.result?._id || item?.result?.result?._id;

          if (!recordId) {
            console.warn(`Warning: No _id found for item at index ${index}`, item);
            return null;
          }

        // Determine h_judgment based on audioJudgments
        let h_judgment_value = null;
        
        if (!hasBuzzDetected ) {
          // In no buzz mode, check individual audio's judgment
          if (audioJudgments[index]) {
            h_judgment_value = audioJudgments[index].judgment;
          } else {
            // Default to false if not explicitly judged
            h_judgment_value = false;
          }
        } else {
          // Buzz detected mode - use selections
          if (selectedOptions[index] === 'buzz') {
            h_judgment_value = true;
          } else if (selectedOptions[index] === 'no_buzz') {
            h_judgment_value = false;
          }
        }

        return { 
          _id: recordId,
          h_judgment: h_judgment_value
        };
        })
        .filter(item => item !== null);

      console.log('Audio recording interface:', audioRecordingInterface);
    console.log('Audio judgments:', audioJudgments);

      const osdVolumeValue = calibrationData.osdVolumeSetting;
      const parsedOsdLevel =
        osdVolumeValue !== "" && osdVolumeValue !== null && osdVolumeValue !== undefined
          ? parseInt(osdVolumeValue, 10)
          : 0;
      const finalOsdLevel = isNaN(parsedOsdLevel) ? 0 : parsedOsdLevel;

    const currentSelection = selectedOptions[currentPage];
      let tagsValue;

      if (!hasBuzzDetected) {
      // Check if ANY audio has buzz judgment
      const hasAnyBuzz = Object.values(audioJudgments).some(judgment => judgment.judgment === true); 
 if (hasAnyBuzz) {
          tagsValue = 'Audio Buzz Corrected';
      } else {
        tagsValue = 'No Buzz';
      }
    }  else {
        if (currentSelection === 'buzz') {
          tagsValue = 'Audio Buzz';
        } else if (currentSelection === 'no_buzz') {
          tagsValue = 'No Buzz Corrected';
        }
      }

      console.log('Tag value:', tagsValue);

      const payload = {
        uploadedBy: userEmail,
        criteria: calibrationData.dutSplCriteria || "",
        osd_volume: calibrationData.osdVolumeSetting || "",
        tags: tagsValue,
        dut_info: {
          model_name: calibrationData.dutModelName || "",
          spl_criteria: calibrationData.dutSplCriteria || ""
        },
        mic_info: {
          mic_model_name: calibrationData.micModelName || "",
          mic_sensitivity: calibrationData.micSensitivity || "",
          audio_interface: calibrationData.audioInterfaceModel || ""
        },
        osd_volume_setting: {
          level: finalOsdLevel
        },
        calibration_action: {
          calibrated: true
        },
        human_verification: {
          result: currentSelection,
          verified_at: new Date().toISOString()
        },
        human_judgment: humanJudgment,
        audio_recording_interface: audioRecordingInterface
      };

      console.log("Submitting payload:", JSON.stringify(payload, null, 2));

      await submitAudioBuzzRecord(payload);

      // ✅ Set flag before navigating to clear data
      sessionStorage.setItem('redirectedFromSubmit', 'true');

      // ✅ Success popup
      await Swal.fire({
        icon: "success",
        title: "Submitted Successfully",
        text: "Result submitted and emailed successfully!",
        confirmButtonColor: "#14b8a6",
        background: "#ffffff",
        color: "#000000",
      });
      setDetectionResults([]);
      setOriginalAudioFiles([]);
      setHumanJudgment(null);

      sessionStorage.setItem('justSubmittedAudioBuzz', 'true');

      // ✅ Navigate - AudioCalibrationTool will clear data on mount
      navigate("/audio-calibaration");

    } catch (error) {
      console.error("Submit error:", error);

      // ❌ Error popup
      await Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.message || "Something went wrong while submitting.",
        confirmButtonColor: "#ef4444",
        background: "#ffffff",
        color: "#000000",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get all risky frequencies for display
  const getAllRiskyFrequencies = () => {
    const allFreqs = new Set();
    Object.values(audioJudgments).forEach(judgment => {
      if (judgment.judgment === true && judgment.frequency) {
        allFreqs.add(judgment.frequency);
      }
    });
    return Array.from(allFreqs).sort((a, b) => a - b);
  };

  // Render NO BUZZ UI (Simple version)
  const renderNoBuzzUI = () => {
    const displayRiskyFrequencies = getAllRiskyFrequencies();
    const currentFreqJudgment = selectedRiskyFrequency ? condition2Judgments[parseInt(selectedRiskyFrequency)] : null;
    const hasAnyBuzz = Object.values(condition2Judgments).some(judgment => judgment === 'buzz');

    return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button onClick={handleBackClick} className="mr-4 text-white hover:text-gray-300">
          <ArrowLeft size={28} />
        </button>
        <h1 className="text-2xl font-bold">
          Audibly verify the system judgment result
        </h1>
      </div>

      {/* Description */}
      <div className="mb-8 text-base leading-relaxed">
        <p>
          In this step, the user is required to listen carefully to the audio output in order to verify whether any audio buzz is present. This manual verification ensures that the system judgment result is accurate.
        </p>
      </div>

      {/* Condition 1 */}
      <div className="mb-10 border-b border-gray-700 pb-8">
        <h2 className="text-xl font-bold mb-6">
          Condition 1: <span className="font-normal">Click "Replay Media" to check if any audio buzz can be heard</span>
        </h2>

      {/* Buttons */}
      <div className="flex gap-3 mb-6 items-center flex-wrap">
          <button
            onClick={handleReplay}
            disabled={isPlaying}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-8 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
          Replay Media
          </button>

          {playingSource === 'replayMedia' && (
        <>
            <button
              onClick={togglePause}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-md"
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>

            <button
              onClick={stopPlayback}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-md"
            >
              Stop
            </button>
        </>
          )}
        </div>

        {/* Basic Frequency Display */}
        <div className="inline-flex items-center gap-3 bg-gray-800 px-4 py-3 rounded-md border border-gray-600">
          <span className="font-medium">Basic Frequency:</span>
          <span className="text-lg font-bold text-blue-400">{currentBasicFrequency}</span>
        </div>
      </div>

      {/* Note */}
      <div className="mb-10 text-base space-y-2 border-b border-gray-700 pb-8">
        <p>
          <span className="font-bold">Note: 1.</span> If no audio buzz is heard, select <span className="font-bold">audio buzz can't be heard</span> in the <span className="font-bold">"Result verified by Ear"</span> field and then submit the result.
        </p>
        <p className="ml-12">
          <span className="font-bold">2.</span> If audio buzz is heard, note suspicious risk frequencies and double-check them using <span className="font-bold">Condition 2</span>.
        </p>
      </div>

      {/* Condition 2 */}
      <div className="mb-10 border-b border-gray-700 pb-8">
        <h2 className="text-xl font-bold mb-6">
          Condition 2: <span className="font-normal">Select suspicious risk frequencies, play one by one to verify and fill in results.</span>
        </h2>

        {/* Frequency Selection and Replay Row */}
        <div className="flex gap-3 items-center flex-wrap mb-6">
      {/* Frequency Dropdown */}
      <select
        value={selectedRiskyFrequency}
        onChange={(e) => setSelectedRiskyFrequency(e.target.value)}
        className="bg-gray-800 text-white border border-gray-600 rounded-md py-2 px-4 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
        disabled={isPlaying}
      >
        <option value="">Select Frequency</option>
        {availableFrequencies.map((freq) => (
          <option key={freq} value={freq}>
            {freq} Hz
          </option>
        ))}
      </select>

          <button
            onClick={handleReplayRiskyFrequencyNoBuzz}
            disabled={isPlaying || !selectedRiskyFrequency}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Replay Risky Frequency
          </button>

          {playingSource === 'riskyFreq' && (
            <>
              <button
                onClick={togglePause}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-md"
              >
                {isPaused ? 'Resume' : 'Pause'}
              </button>

              <button
                onClick={stopPlayback}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-md"
              >
                Stop
              </button>
            </>
          )}
      </div>

        {/* Human Judgment Row */}
        <div className="flex gap-4 items-center flex-wrap">
          <span className="font-bold text-lg">Human Judgment:</span>

          {/* No Buzz Button (Condition 2) */}
        <button
          onClick={() => handleCondition2Judgment('no_buzz')}
          disabled={!selectedRiskyFrequency}
          className={`py-2 px-6 rounded-md font-medium transition-colors border-2 ${
    !selectedRiskyFrequency
      ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
      : currentFreqJudgment === 'no_buzz'
              ? 'bg-green-600 border-green-400 text-white'
              : 'bg-gray-900 border-green-600 text-white hover:bg-gray-800'
            }`}
        >
            ✓ No Buzz
        </button>

          {/* Audio Buzz Button (Condition 2) */}
        <button
          onClick={() => handleCondition2Judgment('buzz')}
         disabled={!selectedRiskyFrequency}
          className={`py-2 px-6 rounded-md font-medium transition-colors border-2 flex items-center gap-2 ${
    !selectedRiskyFrequency
      ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
      : currentFreqJudgment === 'buzz'
              ? 'bg-red-600 border-red-400 text-white'
              : 'bg-gray-900 border-red-600 text-white hover:bg-gray-800'
            }`}
        >
            <span className="text-xl">⚠</span> Audio Buzz
        </button>
        </div>
      </div>

      {/* Result Verified by Ear */}
      <div className="mb-10 border-b border-gray-700 pb-8">
        <div className="flex gap-4 items-center flex-wrap">
          <span className="font-bold text-lg">Result Verified by Ear:</span>

          {/* Audio buzz can't be heard */}
          <button
            onClick={() => {}}
            disabled={!replayMediaCompleted || hasAnyBuzz}
            className={`py-3 px-8 rounded-md font-medium transition-colors border-2 ${
              !replayMediaCompleted || hasAnyBuzz
                ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                : replayMediaCompleted && !hasAnyBuzz
                  ? 'bg-green-600 border-green-400 text-white'
                  : 'bg-gray-900 border-green-600 text-white hover:bg-gray-800'
            }`}
          >
            Audio buzz can't be heard
          </button>

          {/* Audio buzz can be heard (auto-selected, not clickable) */}
          <button
            disabled
            className={`py-3 px-8 rounded-md font-medium border-2 cursor-not-allowed ${
              hasAnyBuzz
                ? 'bg-red-600 border-red-400 text-white'
                : 'bg-gray-700 border-gray-600 text-gray-400'
            }`}
          >
            Audio buzz can be heard
          </button>

          {/* Risky Frequency Display */}
          {displayRiskyFrequencies.length > 0 && (
            <div className="bg-white px-6 py-2 rounded-md border-2 border-gray-800">
              <span className="text-black font-medium">Risky Frequency: </span>
              <span className="text-black font-bold">
                {displayRiskyFrequencies.join(', ')} Hz
              </span>
            </div>
          )}
      </div>
      </div>

      {/* Submit Button */}
      <div className="w-full">
        <button
          onClick={handleSubmit}
          disabled={!replayMediaCompleted || isSubmitting}
          className={`py-3 px-8 rounded-md font-semibold text-sm md:text-base transition-colors w-full ${
            !replayMediaCompleted || isSubmitting
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit and Email the Result'}
        </button>
      </div>
    </div>
  );
  };

  // Render BUZZ DETECTED UI (Detailed spectrum analysis)
  const renderBuzzDetectedUI = () => {
    if (buzzFiles.length === 0) return null;

    const currentBuzzFile = buzzFiles[currentPage];
    const result = currentBuzzFile?.result || {};

    const allHarmonics = result.harmonics || [];
    const avgEnergy = result.mean || 0;
    const stdDeviation = result.std || 0;
    const sumEnergy = avgEnergy + stdDeviation;

    const getHarmonicName = (harmonic_number) => {
      const names = {
        1: 'Basic Frequency',
        2: 'Second Harmonic',
        3: 'Third Harmonic',
        4: 'Fourth Harmonic',
        5: 'Fifth Harmonic'
      };
      return names[harmonic_number] || `Harmonic ${harmonic_number}`;
    };

    return (
      <div className="min-h-screen bg-[#1a2332] text-white p-4">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button onClick={handleBackClick} className="mr-4 text-white hover:text-gray-300">
            <ArrowLeft size={28} />
          </button>
          <h1 className="text-xl md:text-2xl font-bold">
            Possible Audio Buzz Frequency and Spectrum
          </h1>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-300 mb-6">
          Possible audio buzz may occur at {result.base_frequency} Hz. Please refer to the spectrum and harmonic record below.
        </p>

        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row sm:gap-15 gap-6 mb-6">
          {/* Left Column - Spectrum */}
          <div className="flex-1 bg-gray-900 rounded-lg p-3 border border-gray-600">
            <h2 className="sm:text-2xl text-xl font-semibold text-left">Spectrum</h2>

            {/* {result.spectrum_graph_s3_url ? (
              <div className="bg-black rounded-lg p-2 mb-2">
                {isLoading && (
                  <div className="flex items-center justify-center h-64">
                    <LoaderCircle className="animate-spin text-slate-500 w-8 h-8" />
                  </div>
                )}
               
                <img
                  src={result.spectrum_graph_s3_url}
                  alt="Spectrum Graph"
                  className="w-full h-auto max-h-[750px] mx-auto block"
                  onClick={() => setShowImagePreview(true)}
                  onLoad={() => setIsLoading(false)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ESpectrum Graph%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            ) : ( */}
            {result.spectrum_graph_s3_url ? (
              <div className="bg-black rounded-lg p-2 mb-2 relative">
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                    <LoaderCircle className="animate-spin text-slate-500 w-8 h-8" />
                  </div>
                )}
                <img
                  src={result.spectrum_graph_s3_url}
                  alt="Spectrum Graph"
                  className={`w-full h-auto max-h-[750px] mx-auto block transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                  onClick={() => setShowImagePreview(true)}
                  onLoad={() => setIsLoading(false)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ESpectrum Graph%3C/text%3E%3C/svg%3E';
                    setIsLoading(false);
                  }}
                />
              </div>
            ) : (

              <div className="bg-gray-700 rounded-lg p-4 mb-4 h-64 flex items-center justify-center">
                <p className="text-gray-400">Spectrum graph not available</p>
              </div>
            )}

            {/* Replay Button */}
            <button
              onClick={handleReplayRiskyFrequency}
              disabled={isPlaying}
              className={`w-full py-3 px-6 rounded-md font-semibold text-sm flex items-center justify-center gap-2 ${isPlaying
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700 text-white'
                }`}
            >
              <span>▶</span>
              {isPlaying ? 'Playing...' : 'Replay Risky Basic Frequency'}
            </button>
          </div>

          {/* Right Column - Harmonic Frequencies */}
          <div className="bg-[#0f1419] rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Harmonic Frequencies</h2>

            {/* Harmonics Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-2">Harmonic</th>
                    <th className="text-right py-2 px-2">Frequency (Hz)</th>
                    <th className="text-right py-2 px-2">Energy (dB)</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((harmonicNum) => {
                    const harmonic = allHarmonics.find(h => h.harmonic_number === harmonicNum);

                    return (
                      <tr key={harmonicNum} className="border-b border-gray-700">
                        <td className="py-3 px-2">{getHarmonicName(harmonicNum)}</td>
                        <td className="text-right py-3 px-2">
                          {harmonic ? harmonic.frequency : '-'}
                        </td>
                        <td className="text-right py-3 px-2">
                          {harmonic && harmonic.db_level !== null && harmonic.db_level !== undefined
                            ? harmonic.db_level.toFixed(2)
                            : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Statistics */}
                  <tr className="border-b border-gray-700 bg-[#1a2332]">
                    <td className="py-3 px-2 font-medium" colSpan="2">Average energy (excl. basic)</td>
                    <td className="text-right py-3 px-2 font-medium">
                      {result.mean !== null && result.mean !== undefined
                        ? `${result.mean.toFixed(2)} dB`
                        : '-'}
                    </td>
                  </tr>

                  <tr className="border-b border-gray-700 bg-[#1a2332]">
                    <td className="py-3 px-2 font-medium" colSpan="2">Standard deviation</td>
                    <td className="text-right py-3 px-2 font-medium">
                      {result.std !== null && result.std !== undefined
                        ? `${result.std.toFixed(2)} dB`
                        : '-'}
                    </td>
                  </tr>

                  <tr className="bg-[#1a2332]">
                    <td className="py-3 px-2 font-bold" colSpan="2">Sum</td>
                    <td className="text-right py-3 px-2 font-bold">
                      {(result.mean !== null && result.mean !== undefined &&
                        result.std !== null && result.std !== undefined)
                        ? `${sumEnergy.toFixed(2)} dB`
                        : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              Note: Average energy and standard deviation are calculated excluding the fundamental frequency.
            </p>
          </div>
        </div>


        {buzzFiles.length > 1 && (
          <div className="flex justify-center items-center gap-2 mb-6">

            {/* Prev */}
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className={`sm:p-2 p-1 rounded-full ${currentPage === 0
                  ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-teal-600 hover:bg-teal-700 text-white"
                }`}
            >
              <ChevronLeft size={20} />
            </button>

            {/* Pages */}
            <div className="flex gap-1 max-w-[75vw] overflow-x-auto sm:overflow-visible scrollbar-hide">
              {(() => {
                const totalPages = buzzFiles.length;
                const curr = currentPage + 1;
                const pages = [];

                // Always show first page
                pages.push(1);

                let start = curr - 1;
                let end = curr + 1;

                // Near start → show 2,3,4
                if (curr <= 3) {
                  start = 2;
                  end = Math.min(4, totalPages - 1);
                }

                // Near end → show last-3, last-2, last-1
                if (curr >= totalPages - 2) {
                  start = Math.max(totalPages - 3, 2);
                  end = totalPages - 1;
                }

                // Left dots
                if (start > 2) pages.push("...");

                // Middle pages
                for (let i = start; i <= end; i++) {
                  if (i > 1 && i < totalPages) {
                    pages.push(i);
                  }
                }

                // Right dots
                if (end < totalPages - 1) pages.push("...");

                // Always show last page
                if (totalPages > 1) pages.push(totalPages);

                return pages.map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="flex items-center justify-center sm:w-8 w-6 sm:h-8 h-6 text-gray-300"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => {
                        // 👉 LAST PAGE CLICK → SHOW INFO POPUP
                        if (page === totalPages && curr !== page) {
                          Swal.fire({
                            title: "In what did you hear?",
                            html: `
          <p style="margin-bottom:12px;">
            Please select one of the following while submitting:
          </p>

          <div style="text-align:left; margin-left:12px;">
            <p>• Audio buzz can't be heard</p>
            <p>• Audio buzz can be heard</p>
          </div>
        `,
                            confirmButtonText: "OK",
                            confirmButtonColor: "#14b8a6",
                            background: "#ffffff",
                            color: "#000000",
                            allowOutsideClick: false,
                          }).then(() => {
                            // Navigate to last page after OK
                            setCurrentPage(totalPages - 1);
                          });
                        } else {
                          // Normal page navigation
                          setCurrentPage(page - 1);
                        }
                      }}
                      className={`sm:w-8 w-6 sm:h-8 h-6 rounded-full ${curr === page
                          ? "bg-teal-600 text-white"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                    >
                      {page}
                    </button>

                  )
                );
              })()}
            </div>

            {/* Next */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === buzzFiles.length - 1}
              className={`sm:p-2 p-1 rounded-full ${currentPage === buzzFiles.length - 1
                  ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-teal-600 hover:bg-teal-700 text-white"
                }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}



        {/* What did you hear? */}
        <div className="bg-gray-900 rounded-lg p-2 mb-6 border border-gray-800">
          <div
            className={`flex gap-6 ${showSubmit
                ? 'flex-col sm:flex-row sm:justify-between sm:items-start'
                : 'flex-col'
              }`}
          >
            {/* LEFT BLOCK: label + buttons move TOGETHER */}
            <div
              className={`${showSubmit ? 'sm:flex-1' : ''
                }`}
            >
              {/* Label */}
              <h2
                className={`text-lg font-semibold mb-4 text-center ${showSubmit ? 'sm:text-left sm:pl-46' : ''
                  }`}
              >
                What did you hear?
              </h2>

              {/* Buttons */}
              <div
                className={`flex gap-4 flex-wrap ${showSubmit
                    ? 'justify-center sm:justify-start'
                    : 'justify-center'
                  }`}
              >
                <button
                  onClick={() => handleOptionSelect('no_buzz')}
                  className={`py-3 px-8 rounded-md font-medium text-sm transition-colors flex items-center gap-2 ${selectedOptions[currentPage]  === 'no_buzz'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-white border border-green-600'
                    }`}
                >
                  <HeadphoneOff className="w-6 h-6 flex-shrink-0" />
                  Audio buzz can't be heard
                </button>

                <button
                  onClick={() => handleOptionSelect('buzz')}
                  className={`py-3 px-8 rounded-md font-medium text-sm transition-colors flex items-center gap-2 ${selectedOptions[currentPage]  === 'buzz'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-white border border-red-600'
                    }`}
                >
                  <Headphones className="w-6 h-6 flex-shrink-0" />
                  Audio buzz can be heard
                </button>
              </div>
            </div>

            {/* RIGHT BLOCK: Submit (only when present) */}
            {showSubmit && (
              <div className="flex flex-col items-start gap-3 sm:flex-shrink-0">
                <span className="text-lg font-semibold text-white">
                  Submit
                </span>

                <button
                  onClick={handleSubmit}
                  disabled={!selectedOptions[currentPage]  || isSubmitting}
                  className={`py-3 px-8 rounded-md font-semibold transition-colors flex items-center gap-2 ${!selectedOptions[currentPage]  || isSubmitting
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  {isSubmitting ? 'Submitting...' : 'Submit and Email Result'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Image Preview Modal */}
        {showImagePreview && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowImagePreview(false);
            }}
          >
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setShowImagePreview(false)}
                className="
                  absolute -top-15 -right-3
                  sm:-top-14 sm:-right-5
                  bg-gray-800 hover:bg-gray-700
                  text-white rounded-full
                  w-9 h-9 sm:w-10 sm:h-10
                  flex items-center justify-center
                  text-xl font-bold z-20
                "
              >
                ×
              </button>

              {/* Preview container */}
              <div className="bg-gray-900 max-w-6xl max-h-[98vh] overflow-auto relative flex items-center justify-center">
                {/* Loader */}
                {isImageLoading && (
                  <LoaderCircle className="w-10 h-10 text-white animate-spin absolute" />
                )}

                {/* Preview image */}
                <img
                  src={result.spectrum_graph_s3_url}
                  alt="Spectrum Graph Preview"
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                  className={`
                    w-full max-w-full
                    h-auto
                    max-h-[95vh]
                    sm:max-h-[90vh]
                    lg:max-h-[98vh]
                    object-contain
                    ${isImageLoading ? "opacity-0" : "opacity-100"}
                    transition-opacity duration-300
                  `}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handler for buzz detected UI
  const handleOptionSelect = (option) => {
    setSelectedOptions((prev) => {
      if (prev[currentPage] === option) {
        return {
          ...prev,
          [currentPage]: null
        };
      }
      
      return {
        ...prev,
        [currentPage]: option
      };
    });
  };

  // Main render - decide which UI to show
  return hasBuzzDetected ? renderBuzzDetectedUI() : renderNoBuzzUI();
};

export default AudioVerification;